// Storage of the daily review record - see doc/daily-review-concept.org §5.2.
//
// One persistent file (`reviewPath`, default /roam/review.org) holding the whole
// practice, rather than a node per daily note: the reviews and plans are
// daily-cadence but the values list is standing and the thankfulness is
// deliberately weekly, and splitting those across three storage shapes would buy
// nothing. Written as real Org headlines and plain lists, never a properties
// drawer, so the record stays a first-class Org artifact:
//
//   * Tagesrückblick :daily-review:
//   ** Values
//   - being someone my kids copy on purpose
//   - work that is honest before it is impressive
//   ** Reviews
//   - [2026-08-10 Mon] the difficult call with the supplier
//     - taught :: I concede too early when someone raises their voice
//     - differently :: say "let me think and come back" instead of agreeing
//     - served :: work that is honest before it is impressive
//     - rough :: yes
//   ** Plans
//   - [2026-08-10 Mon] if the supplier pushes again :: I will end the call and write instead
//     - arose :: yes
//     - kept :: no
//   ** Thankfulness
//   - [2026-08-09 Sun]
//     - for :: the walk we took before it rained :: nobody looked at a phone
//     - for :: R. covering my shift :: he offered before I asked
//
// Conventions, all load-bearing and all shared with lib/team_org.js and
// lib/formation_org.js:
//
//   - The node is found by its *tag*, never its title, so the headline is copy.
//   - No TODO/DONE keyword anywhere: nothing here is a task to be completed, and
//     a review is not something one completes.
//   - Inactive timestamps throughout - a record of what happened, and none of it
//     belongs in tomorrow's agenda.
//   - Every text field is one line, folded on the way in by lib/review.js.
//
// Read and written by line-level text surgery rather than a parseOrg/exportOrg
// round trip: the file is the user's, and the exporter could reformat content
// this node never touched. Surgery replaces the tagged node's entire subtree and
// leaves everything outside it alone - so nothing should be hand-added inside
// the node, since the next save regenerates it wholesale.

import { normalizeReviewState } from './review';

export const REVIEW_TAG = 'daily-review';
export const REVIEW_HEADLINE = 'Tagesrückblick';

export const VALUES_TITLE = 'Values';
export const REVIEWS_TITLE = 'Reviews';
export const PLANS_TITLE = 'Plans';
export const THANKFULNESS_TITLE = 'Thankfulness';

// Separates the two halves of a plan's primary line, and the thing from its why
// in a thankfulness item. A field containing this sequence and nothing before it
// would read back split in the wrong place; accepted, because escaping it would
// make the file worse for the human reading it in Emacs, and lib/review.js folds
// every field to one line so the damage is bounded to that one entry.
const SEPARATOR = ' :: ';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A day key as an inactive org timestamp: 2026-08-10 -> [2026-08-10 Mon]. */
export const formatOrgDay = (key) => {
  const [year, month, day] = `${key}`.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(year, month - 1, day).getDay()];

  return `[${key} ${weekday}]`;
};

// A leading date in either timestamp form, active or inactive - only the day is
// ever read back out, since nothing here records a time of day.
const LEADING_DAY_REGEX = /^[[<](\d{4}-\d{2}-\d{2})[^\]>]*[\]>]\s*/;

const headlineLevel = (line) => {
  const match = /^(\*+)\s/.exec(line);
  return match ? match[1].length : null;
};

const trailingTags = (text) => {
  const match = /:((?:[^\s:]+:)+)\s*$/.exec(text);
  return match ? match[1].split(':').filter(Boolean) : [];
};

/** A headline's own text: stars and trailing tags removed. */
const headlineTitle = (line) =>
  `${line}`
    .replace(/^\*+\s+/, '')
    .replace(/:(?:[^\s:]+:)+\s*$/, '')
    .trim();

// Exactly one star and the review tag: the user asked for the main node, and a
// deeper or differently-tagged headline of the same title is somebody else's.
const isReviewHeadline = (line) =>
  headlineLevel(line) === 1 && trailingTags(line).some((tag) => tag.toLowerCase() === REVIEW_TAG);

/** Index one past the last line belonging to the subtree that starts at `index`. */
const endOfSubtreeIndex = (lines, index) => {
  let end = index + 1;
  while (end < lines.length) {
    const level = headlineLevel(lines[end]);
    if (level !== null && level <= 1) break;
    end += 1;
  }

  // A blank-line separator right before the next headline (or EOF) is spacing
  // between subtrees, not part of this one - excluding it from what gets
  // replaced means resaving does not eat that spacing line by line.
  while (end > index + 1 && lines[end - 1] === '') end -= 1;

  return end;
};

// A list item beginning with a star would read back as a headline and cut the
// subtree in half. Org's own escape for that is a leading comma.
const escapeItem = (text) => (/^\s*\*/.test(text) ? `,${text}` : text);

const unescapeItem = (text) => `${text}`.replace(/^,(\s*\*)/, '$1').trim();

const splitOnSeparator = (text) => {
  const index = `${text}`.indexOf(SEPARATOR);
  if (index === -1) return null;

  return [text.slice(0, index).trim(), text.slice(index + SEPARATOR.length).trim()];
};

const buildNode = (state) => {
  const { values, reviews, plans, thankfulness } = normalizeReviewState(state);
  const lines = [`* ${REVIEW_HEADLINE} :${REVIEW_TAG}:`];

  // Every section keeps its headline even while empty: the node is a form the
  // page fills in, and a section that vanishes when empty reads like a bug.
  lines.push(`** ${VALUES_TITLE}`);
  values.forEach((value) => lines.push(`- ${escapeItem(value)}`));

  lines.push(`** ${REVIEWS_TITLE}`);
  reviews.forEach((entry) => {
    lines.push(`- ${formatOrgDay(entry.date)} ${escapeItem(entry.happened)}`);
    if (entry.taught) lines.push(`  - taught :: ${entry.taught}`);
    if (entry.differently) lines.push(`  - differently :: ${entry.differently}`);
    if (entry.served) lines.push(`  - served :: ${entry.served}`);
    if (entry.rough) lines.push('  - rough :: yes');
    if (entry.affirmed) lines.push(`  - affirmed :: ${entry.affirmed}`);
  });

  lines.push(`** ${PLANS_TITLE}`);
  plans.forEach((plan) => {
    lines.push(
      `- ${formatOrgDay(plan.date)} ${escapeItem(plan.situation)}${SEPARATOR}${plan.action}`
    );
    if (plan.arose) lines.push(`  - arose :: ${plan.arose}`);
    if (plan.kept) lines.push(`  - kept :: ${plan.kept}`);
  });

  lines.push(`** ${THANKFULNESS_TITLE}`);
  thankfulness.forEach((entry) => {
    lines.push(`- ${formatOrgDay(entry.date)}`);
    entry.items.forEach((item) =>
      lines.push(`  - for :: ${item.thing}${item.why ? `${SEPARATOR}${item.why}` : ''}`)
    );
  });

  return `${lines.join('\n')}\n`;
};

/**
 * The file contents with the review record written as the review-tagged node -
 * appended when the file has none yet, and the whole file is created when there
 * is no file at all (contents null).
 *
 * The node's entire subtree is replaced. Nothing outside it is touched.
 */
export const upsertReviewNode = (contents, state) => {
  const node = buildNode(state);

  if (contents === null || contents === undefined || contents === '') {
    return node;
  }

  const lines = contents.split('\n');
  const headlineIndex = lines.findIndex((line) => isReviewHeadline(line));

  if (headlineIndex === -1) {
    const terminated = contents.endsWith('\n') ? contents : `${contents}\n`;
    return `${terminated}\n${node}`;
  }

  const end = endOfSubtreeIndex(lines, headlineIndex);
  const nodeLines = node.split('\n');
  nodeLines.pop(); // the trailing '' left by node's closing newline

  return [...lines.slice(0, headlineIndex), ...nodeLines, ...lines.slice(end)].join('\n');
};

const LIST_ITEM_REGEX = /^-\s+(.*)$/;
const SUB_ITEM_REGEX = /^\s+-\s+(taught|differently|served|rough|affirmed|arose|kept|for)\s*::\s*(.*)$/;

const SECTION_BY_TITLE = {
  [VALUES_TITLE.toLowerCase()]: 'values',
  [REVIEWS_TITLE.toLowerCase()]: 'reviews',
  [PLANS_TITLE.toLowerCase()]: 'plans',
  [THANKFULNESS_TITLE.toLowerCase()]: 'thankfulness',
};

/**
 * The review record out of its file, in the shape lib/review.js works on, or
 * null when the file has no review-tagged node at all (so the caller can tell
 * "no record yet" from "a record with nothing in it").
 *
 * Everything unrecognised inside the node is ignored rather than guessed at: the
 * file is hand-editable and one mangled line must not cost the rest.
 */
export const extractReviewFromFile = (contents) => {
  if (!contents) return null;

  const lines = contents.split('\n');
  const headlineIndex = lines.findIndex((line) => isReviewHeadline(line));
  if (headlineIndex === -1) return null;

  const end = endOfSubtreeIndex(lines, headlineIndex);

  const values = [];
  const reviews = [];
  const plans = [];
  const thankfulness = [];

  let section = null;

  for (let index = headlineIndex + 1; index < end; index += 1) {
    const line = lines[index];

    if (headlineLevel(line) !== null) {
      section = SECTION_BY_TITLE[headlineTitle(line).toLowerCase()] || null;
      continue;
    }

    if (!line.trim()) continue;

    const subMatch = SUB_ITEM_REGEX.exec(line);
    if (subMatch) {
      const [, field, rawValue] = subMatch;
      const value = rawValue.trim();

      if (section === 'reviews' && reviews.length) {
        const last = reviews[reviews.length - 1];
        if (field === 'rough') last.rough = value.toLowerCase() === 'yes';
        else if (['taught', 'differently', 'served', 'affirmed'].includes(field)) {
          last[field] = value;
        }
      } else if (section === 'plans' && plans.length) {
        const last = plans[plans.length - 1];
        if (field === 'arose' || field === 'kept') last[field] = value.toLowerCase();
      } else if (section === 'thankfulness' && thankfulness.length && field === 'for') {
        const parts = splitOnSeparator(value);
        thankfulness[thankfulness.length - 1].items.push(
          parts ? { thing: parts[0], why: parts[1] } : { thing: value, why: '' }
        );
      }
      continue;
    }

    const itemMatch = LIST_ITEM_REGEX.exec(line);
    if (!itemMatch) continue;

    // The values list is the one section whose items carry no date.
    if (section === 'values') {
      values.push(unescapeItem(itemMatch[1]));
      continue;
    }

    const dateMatch = LEADING_DAY_REGEX.exec(itemMatch[1]);
    if (!dateMatch) continue;

    const date = dateMatch[1];
    const rest = unescapeItem(itemMatch[1].slice(dateMatch[0].length));

    if (section === 'reviews') {
      reviews.push({
        date,
        happened: rest,
        taught: '',
        differently: '',
        served: '',
        rough: false,
        affirmed: '',
      });
    } else if (section === 'plans') {
      const parts = splitOnSeparator(rest);
      // A plan is both halves or it is not a plan; normalizePlans drops the rest.
      plans.push({
        date,
        situation: parts ? parts[0] : rest,
        action: parts ? parts[1] : '',
        arose: null,
        kept: null,
      });
    } else if (section === 'thankfulness') {
      thankfulness.push({ date, items: [] });
    }
  }

  return normalizeReviewState({ values, reviews, plans, thankfulness });
};
